import { useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { UiProvider } from '@wbk/ui';
import { WbkTicketingProvider } from '@wbk/ticketing';
import { AnalyticsProvider } from '@wbk/analytics';
import { AuthProvider } from '@wbk/auth';
import { WbkConfigProvider } from '@wbk/config';
import { CurrencyProvider } from '@wbk/currency';
import { createCookie } from '@wbk/utils';
import { QueueProvider } from '@wbk/queue';
import { useGetSupportedCurrencies } from '@wbk/api/hooks';
import { POSTER_1X1_FADED, POSTER_3X1_FADED } from '../constants/fallbackLogo';
import { gtmTracker } from '../../trackers.config';

type Props = {
  children: React.ReactNode;
};

const FALLBACK_CURRENCY = 'SAR';

const AppProviders = ({ children }: Props) => {
  const { locale = 'en', pathname, push, replace, locales, asPath, query } = useRouter();
  const { t, i18n } = useTranslation();
  const lang = locale;
  const { data } = useGetSupportedCurrencies({ lang });
  const currencies = data ?? [];
  const detectedCurrency = FALLBACK_CURRENCY;

  const search = useMemo(() => new URLSearchParams(asPath.split('?')[1]), [asPath]);

  // Mirrors react-router's setSearchParams: replaces the whole query, so setSearch({}) clears it
  const setSearch = (
    newSearch: Record<string, string | string[]>,
    options?: { replace: boolean }
  ) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(newSearch)) {
      const values = Array.isArray(value) ? value : [value];
      values.forEach((v) => params.append(key, v));
    }

    const [path] = asPath.split('?');
    const query = params.toString();
    const url = query ? `${path}?${query}` : path;

    const navigate = options?.replace ? replace : push;
    navigate(url, undefined, { shallow: true });
  };

  const setLocale = (goto: string) => {
    createCookie({
      name: 'lang',
      value: goto,
      expiration: 30,
    });
    document.documentElement.dir = i18n.dir(goto);
    push(asPath, undefined, { locale: goto });
  };

  return (
    <WbkConfigProvider
      i18n={{ useTranslation, Trans, languages: locales || [] }}
      router={{
        Link,
        push,
        replace,
        locale,
        search,
        setSearch,
        setLocale,
        params: query as Record<string, string>,
      }}
      appSource={process.env.NEXT_PUBLIC_APP_SOURCE!}
      subscriptions={{ active: false }}
      cf={{
        turnstileSiteKey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
      }}
      logo='/icons/logo.png'
    >
      <QueueProvider>
        <UiProvider imgFallbacks={{ '1x1': POSTER_1X1_FADED, '3x1': POSTER_3X1_FADED }}>
          <AnalyticsProvider trackers={[gtmTracker]} currentLocation={pathname}>
            <AuthProvider
              autoInit={false}
              newsLetter={{ organizationName: t('common:title') }}
              poweredBy
            >
              <CurrencyProvider
                detectedCurrency={detectedCurrency}
                currencies={currencies.map((c) => ({
                  ...c,
                  shortCode: c.short_code,
                  symbol: c.currency_symbol || '',
                  svgPath: c.svg_path,
                  svgViewbox: c.svg_viewbox,
                }))}
              >
                <WbkTicketingProvider
                  seats={{
                    workspace: {
                      seatIO: process.env.NEXT_PUBLIC_SEATIO_WORKSPACE_KEY,
                      seatCloud: process.env.NEXT_PUBLIC_SEATCLOUD_WORKSPACE_KEY,
                    },
                  }}
                  resell={{
                    terms: t('booking:resell_terms_agree'),
                  }}
                >
                  {children}
                </WbkTicketingProvider>
              </CurrencyProvider>
            </AuthProvider>
          </AnalyticsProvider>
        </UiProvider>
      </QueueProvider>
    </WbkConfigProvider>
  );
};

export default AppProviders;
